// src/pages/motolivraison/commande-detail.page.jsx
//
// Détail d'une commande avec actions admin : attribution manuelle livreur, annulation.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Descriptions, Table, Modal, Form, Select, Input,
  Button, message, Space, Breadcrumb, Spin, Steps, Avatar
} from 'antd';
import {
  ArrowLeftOutlined, RightOutlined, UserAddOutlined, CloseCircleOutlined,
  ShopOutlined, UserOutlined, PhoneOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { motolivraisonService } from '../../common/services';

const STATUS_LABELS = {
  en_attente_paiement: 'Attente paiement',
  payee: 'Payée',
  en_attente_livreur: 'Attente livreur',
  assignee: 'Assignée',
  recuperee: 'Récupérée',
  en_livraison: 'En livraison',
  livree: 'Livrée',
  confirmee: 'Confirmée',
  soldee: 'Soldée',
  annulee: 'Annulée',
};

const TIMELINE_STEPS = [
  'payee', 'en_attente_livreur', 'assignee', 'recuperee', 'en_livraison', 'livree', 'confirmee', 'soldee'
];

const CommandeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commande, setCommande] = useState(null);
  const [loading, setLoading] = useState(true);
  const [livreurs, setLivreurs] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [assignForm] = Form.useForm();
  const [cancelForm] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getCommandeById(id);
      setCommande(data);
    } catch (e) {
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const openAssign = async () => {
    try {
      const data = await motolivraisonService.listLivreurs({ status: 'approved' });
      setLivreurs(data?.livreurs || []);
      setAssignModalOpen(true);
    } catch (e) {
      message.error('Erreur lors du chargement des livreurs');
    }
  };

  const handleAssign = async () => {
    try {
      const { livreurId } = await assignForm.validateFields();
      await motolivraisonService.assignerLivreur(id, livreurId);
      message.success('Livreur assigné');
      setAssignModalOpen(false);
      fetch();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur');
    }
  };

  const handleCancel = async () => {
    try {
      const { reason } = await cancelForm.validateFields();
      await motolivraisonService.cancelCommande(id, reason);
      message.success('Commande annulée');
      setCancelModalOpen(false);
      fetch();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur');
    }
  };

  if (loading || !commande) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  const currentStepIdx = TIMELINE_STEPS.indexOf(commande.statut);
  const isCancelled = commande.statut === 'annulee';
  const canAssign = ['en_attente_livreur', 'payee'].includes(commande.statut);
  const canCancel = !['confirmee', 'soldee', 'annulee'].includes(commande.statut);

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/commandes">Commandes</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{id.slice(-8)}</Breadcrumb.Item>
        </Breadcrumb>

        <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>Retour</Button>

        {/* Timeline */}
        <Card title="Suivi de la commande" style={{ marginBottom: 16 }}>
          {isCancelled ? (
            <div style={{ background: '#fff1f0', padding: 16, borderRadius: 8, border: '1px solid #ffa39e' }}>
              <h4 style={{ color: '#cf1322' }}>Commande annulée</h4>
              {commande.raisonAnnulation && <p>{commande.raisonAnnulation}</p>}
            </div>
          ) : (
            <Steps
              current={currentStepIdx >= 0 ? currentStepIdx : 0}
              size="small"
              items={TIMELINE_STEPS.map(s => ({ title: STATUS_LABELS[s] }))}
            />
          )}
        </Card>

        {/* Infos principales */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card title="Commerce" size="small">
              <Space direction="vertical">
                <div><ShopOutlined /> {commande.commerceId?.nom || '—'}</div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Client" size="small">
              <Space direction="vertical">
                <div><UserOutlined /> {commande.clientNom || '—'}</div>
                <div><PhoneOutlined /> {commande.clientPhone}</div>
                <div><EnvironmentOutlined /> {commande.adresseLivraison?.libelle}</div>
                {commande.adresseLivraison?.quartier && (
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>Quartier : {commande.adresseLivraison.quartier}</div>
                )}
                {commande.adresseLivraison?.latitude != null && commande.adresseLivraison?.longitude != null && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${commande.adresseLivraison.latitude},${commande.adresseLivraison.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <EnvironmentOutlined /> Ouvrir la position GPS dans Google Maps
                  </a>
                )}
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Livreur si assigné */}
        {commande.livreurId && (
          <Card title="Livreur assigné" size="small" style={{ marginTop: 16 }}>
            <Space>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <div>
                <div>{commande.livreurId.firstName} {commande.livreurId.lastName}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{commande.livreurId.phone}</div>
              </div>
            </Space>
          </Card>
        )}

        {/* Articles */}
        <Card title="Articles" style={{ marginTop: 16 }}>
          <Table
            size="small"
            pagination={false}
            dataSource={(commande.articles || []).map((a, i) => ({ ...a, key: i }))}
            columns={[
              { title: 'Article', dataIndex: 'nom' },
              { title: 'Prix unit.', dataIndex: 'prixUnitaire', align: 'right', render: v => `${v} F` },
              { title: 'Qté', dataIndex: 'quantite', align: 'center', width: 60 },
              { title: 'Sous-total', dataIndex: 'sousTotal', align: 'right', render: v => <strong>{v} F</strong> },
            ]}
          />
        </Card>

        {/* Montants */}
        <Card title="Montants" style={{ marginTop: 16 }}>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Articles HT">{commande.montantArticles} F</Descriptions.Item>
            <Descriptions.Item label="Commission TIGOON">{commande.commissionMontant} F</Descriptions.Item>
            <Descriptions.Item label="Total payé client">
              <strong style={{ fontSize: 18 }}>{commande.montantTTC} F</strong>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Code SMS si mode téléphone */}
        {commande.codeSMSConfirmation && (
          <Card title="Code de confirmation SMS" style={{ marginTop: 16 }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', textAlign: 'center', letterSpacing: 8, color: '#fa8c16' }}>
              {commande.codeSMSConfirmation}
            </div>
          </Card>
        )}

        {/* Actions admin */}
        <Card title="Actions administrateur" style={{ marginTop: 16 }}>
          <Space wrap>
            {canAssign && (
              <Button type="primary" icon={<UserAddOutlined />} onClick={openAssign}>
                Attribuer manuellement à un livreur
              </Button>
            )}
            {canCancel && (
              <Button danger icon={<CloseCircleOutlined />} onClick={() => setCancelModalOpen(true)}>
                Annuler la commande
              </Button>
            )}
          </Space>
        </Card>

        {/* Métadonnées */}
        <Card title="Métadonnées" style={{ marginTop: 16 }}>
          <Descriptions size="small" column={2}>
            <Descriptions.Item label="ID">{commande._id}</Descriptions.Item>
            <Descriptions.Item label="Mode">{commande.mode}</Descriptions.Item>
            <Descriptions.Item label="Paiement">
              {commande.mode === 'telephone' || commande.modePaiement === 'wave_livraison'
                ? 'Wave à la livraison'
                : 'Wave prépayé'}
            </Descriptions.Item>
            <Descriptions.Item label="Créée le">{dayjs(commande.createdAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
            {commande.payeeAt && <Descriptions.Item label="Payée le">{dayjs(commande.payeeAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
            {commande.assigneeAt && <Descriptions.Item label="Assignée le">{dayjs(commande.assigneeAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
            {commande.livreeAt && <Descriptions.Item label="Livrée le">{dayjs(commande.livreeAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
          </Descriptions>
        </Card>
      </div>

      {/* Modal Attribution livreur */}
      <Modal
        title="Attribuer manuellement un livreur"
        open={assignModalOpen}
        onCancel={() => setAssignModalOpen(false)}
        onOk={handleAssign}
        okText="Attribuer"
        cancelText="Annuler"
      >
        <Form form={assignForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="livreurId"
            label="Livreur"
            rules={[{ required: true, message: 'Veuillez choisir un livreur' }]}
          >
            <Select
              placeholder="Sélectionner un livreur approuvé"
              showSearch
              optionFilterProp="label"
              options={livreurs.map(l => ({
                value: l._id,
                label: `${l.firstName} ${l.lastName} — ${l.phone}${l.livreurMotoProfile?.isAvailable ? ' 🟢' : ''}`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Annulation */}
      <Modal
        title="Annuler la commande"
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        onOk={handleCancel}
        okText="Confirmer l'annulation"
        cancelText="Retour"
        okButtonProps={{ danger: true }}
      >
        <Form form={cancelForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="reason"
            label="Raison de l'annulation"
            rules={[{ required: true, message: 'La raison est requise' }]}
          >
            <Input.TextArea rows={3} placeholder="Pourquoi annulez-vous cette commande ?" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CommandeDetail;
